import { useEffect, useState } from "react";
import { DJSEMApiClient, PanelShell, Button, TextField, Badge, tokens } from "@djsem/ui";

// Perpetual desktop license purchase flow (task #45, Phase E / prompt 147). This is a plain web
// page, not part of the shared DJSEMWorkspaceApp: purchase happens before/without any desktop
// install or account session (the backend routes are public — see
// apps/analysis-worker/src/routes/licenseHandlers.ts), so someone can buy a license before ever
// opening the Studio app. Activation (turning a purchased key into a working license on a given
// machine) DOES live in the shared renderer — see libs/djsem-ui/src/modules/SettingsModule.tsx's
// LicensePanel — since that step needs the desktop machine id.
//
// apps/web has no router (see main.tsx's "thin mount" comment); these three pages are reached via
// a plain window.location.pathname check in main.tsx, matching that same minimal-infra style.

// Prices mirror @djsem/shared's LICENSE_PRICE_CENTS exactly (creator £99, the rest £199) — kept
// as a separate display-only copy rather than imported, since this app doesn't otherwise depend
// on @djsem/shared and the real number only needs to be shown, not computed. Exported so
// LicensePages.test.ts can assert this list never drifts from @djsem/shared's LICENSABLE_TIERS
// (only creator/creator_pro/performer/pro_integrations are self-serve purchasable — issue #18).
export const TIERS: Array<{ value: string; label: string; priceLabel: string }> = [
  { value: "creator", label: "Creator", priceLabel: "£99 one-time" },
  { value: "creator_pro", label: "Creator Pro", priceLabel: "£199 one-time" },
  { value: "performer", label: "Performer", priceLabel: "£199 one-time" },
  { value: "pro_integrations", label: "Pro Integrations", priceLabel: "£199 one-time" },
];

// Stripe's success/cancel URLs are static (no session/license id template — see
// licensePurchaseCreate in licenseHandlers.ts), so the license id has to survive the redirect
// round-trip via sessionStorage rather than a query param the backend doesn't send.
const PENDING_KEY = "djsem_pending_license";
interface PendingLicense { licenseId: string; email: string; tier: string }

// Issue #18: the submit button previously only checked `!email.trim()`, so any non-empty
// malformed email (e.g. "not-an-email") reached the purchase call. Same shape as
// @djsem/shared's validateLicenseEmail (and the backend's own check) — inlined rather than
// importing @djsem/shared for one regex, consistent with this file's existing "display-only
// copy, not a new dependency" choice for TIERS' prices above.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True for a non-empty, `local@domain.tld`-shaped email. Exported for tests only. */
export function isValidPurchaseEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

function PurchaseErrorMessage({ error }: { error: unknown }) {
  const code = typeof error === "string" ? error : null;
  const copy: Record<string, string> = {
    server_misconfigured: "Purchasing is temporarily unavailable. Please try again shortly.",
  };
  const text = code ? (copy[code] ?? `Checkout couldn't be started (${code}).`) : "Something went wrong — please try again.";
  return <div style={{ color: tokens.color.danger, fontSize: 13, marginTop: 10 }}>{text}</div>;
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: tokens.color.bg, padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 18, fontFamily: "\"Cormorant Garamond\", Georgia, serif", fontSize: 22, letterSpacing: "0.1em", color: tokens.color.textDim }}>
          DJSEM
        </div>
        {children}
      </div>
    </div>
  );
}

export function LicensePurchasePage({ client }: { client: DJSEMApiClient }) {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState<string>(TIERS[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const emailValid = isValidPurchaseEmail(email);
  // TextField (@djsem/ui) has no onBlur hook, so "show the error" is gated on there being any
  // content at all rather than a separate touched flag — still never nags on the empty field.
  const showEmailError = email.trim().length > 0 && !emailValid;

  const submit = async () => {
    if (!emailValid) return;
    setSubmitting(true);
    setError(undefined);
    const res = await client.purchaseLicense(email.trim(), tier);
    if (res.ok && res.data.checkoutUrl) {
      const pending: PendingLicense = { licenseId: res.data.licenseId, email: email.trim(), tier };
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
      window.location.href = res.data.checkoutUrl;
      return;
    }
    setError(res.ok ? "no_checkout_url" : res.error);
    setSubmitting(false);
  };

  return (
    <PageShell>
      <PanelShell title="Buy a perpetual desktop license">
        <p style={{ fontSize: 13, color: tokens.color.textDim, marginTop: 0 }}>
          One-time purchase, no subscription. After checkout you'll get a license key to activate
          in the DJSEM desktop app.
        </p>
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, marginBottom: 4 }}>Email</label>
        <TextField
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          type="email"
          style={{ width: "100%", boxSizing: "border-box" }}
        />
        {showEmailError ? (
          <div style={{ color: tokens.color.danger, fontSize: 12, marginTop: 4 }}>Enter a valid email address.</div>
        ) : null}
        <label style={{ display: "block", fontSize: 12, color: tokens.color.muted, margin: "12px 0 4px" }}>Tier</label>
        <select value={tier} onChange={(e) => setTier(e.target.value)}
          style={{ width: "100%", padding: 8, fontSize: 12, background: tokens.color.bg, color: tokens.color.text, border: `1px solid ${tokens.color.border}`, borderRadius: 4 }}>
          {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label} — {t.priceLabel}</option>)}
        </select>
        <div style={{ fontSize: 20, color: tokens.color.text, margin: "12px 0" }}>
          {TIERS.find((t) => t.value === tier)?.priceLabel}
        </div>
        <Button variant="primary" onClick={() => void submit()} disabled={submitting || !emailValid}>
          {submitting ? "Starting checkout…" : "Continue to checkout"}
        </Button>
        {error !== undefined ? <PurchaseErrorMessage error={error} /> : null}
      </PanelShell>
    </PageShell>
  );
}

export function LicenseSuccessPage({ client }: { client: DJSEMApiClient }) {
  const [pending] = useState<PendingLicense | null>(() => {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as PendingLicense; } catch { return null; }
  });
  const [status, setStatus] = useState<"loading" | "paid" | "pending" | "error">("loading");
  const [licenseKey, setLicenseKey] = useState<string | null>(null);

  const check = async () => {
    if (!pending) { setStatus("error"); return; }
    setStatus("loading");
    const res = await client.getLicenseStatus(pending.licenseId);
    if (!res.ok) { setStatus("error"); return; }
    if (res.data.license.status === "paid") {
      setLicenseKey(res.data.license.licenseKey);
      setStatus("paid");
      sessionStorage.removeItem(PENDING_KEY);
    } else {
      setStatus("pending");
    }
  };

  // Run once on mount only — re-running on every render (check's identity isn't memoized) would
  // refetch in a loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void check(); }, []);

  return (
    <PageShell>
      <PanelShell title="Checkout complete">
        {!pending ? (
          <p style={{ fontSize: 13, color: tokens.color.textDim }}>
            We can't find a pending purchase in this browser session. If you just paid, check the
            confirmation email — the Stripe receipt includes your license key.
          </p>
        ) : status === "loading" ? (
          <p style={{ fontSize: 13, color: tokens.color.textDim }}>Confirming your payment…</p>
        ) : status === "paid" && licenseKey ? (
          <>
            <p style={{ fontSize: 13, color: tokens.color.textDim }}>Thanks — your license is ready.</p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <Badge tone="ok">Paid</Badge>
              <code style={{ fontSize: 14, padding: "4px 8px", background: tokens.color.surface2, borderRadius: 4 }}>{licenseKey}</code>
            </div>
            <p style={{ fontSize: 12, color: tokens.color.muted, marginTop: 10 }}>
              Open the DJSEM desktop app → Settings → License, and enter this key to activate it on this machine.
            </p>
            <p style={{ fontSize: 12, color: tokens.color.muted, marginTop: 6 }}>
              This license binds to one machine at a time — activating it elsewhere won't move it
              automatically. Moving to a new machine, or a lost/replaced device, needs a support
              reset before the key will activate somewhere else.
            </p>
          </>
        ) : status === "pending" ? (
          <>
            <p style={{ fontSize: 13, color: tokens.color.textDim }}>
              Payment is still confirming — this can take a few seconds.
            </p>
            <Button onClick={() => void check()}>Check again</Button>
          </>
        ) : (
          <p style={{ fontSize: 13, color: tokens.color.danger }}>
            Couldn't confirm this purchase right now. Check the confirmation email for your license key, or try again shortly.
          </p>
        )}
      </PanelShell>
    </PageShell>
  );
}

export function LicenseCancelledPage() {
  return (
    <PageShell>
      <PanelShell title="Checkout cancelled">
        <p style={{ fontSize: 13, color: tokens.color.textDim }}>No payment was made.</p>
        <a href="/license" style={{ color: tokens.color.accent, fontSize: 13 }}>Back to purchase a license</a>
      </PanelShell>
    </PageShell>
  );
}
